import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    AdornedInput, Adornment, FormField, FormHelp, Label, Paragraph,
} from '@zillow/constellation';
import { CurrencyInput, Col, Row } from '@zillow/zrs-comet';

import RentGuaranteeActions from 'app/shared/actions/RentGuaranteeActions';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';

import RadioButton from '../components/RadioButton';

const WAIVE_SECURITY_DEPOSIT = 'waiveSecurityDeposit';
const REQUIRE_SECURITY_DEPOSIT = 'requireSecurityDeposit';

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
};
const defaultProps = {};

const VerifySecurityDeposit = ({
    onGoToNextStep,
    onGoToPrevStep,
}) => {
    const dispatch = useDispatch();
    const securityDepositSelector = useSelector(state => state.rentGuarantee.securityDeposit);
    const [securityDepositChoice, setSecurityDepositChoice] = useState(securityDepositSelector);
    const [securityDeposit, setSecurityDeposit] = useState(null);

    const onSecurityDepositChange = (event) => {
        const value = Number(event.target.value);
        setSecurityDeposit(value);
    };

    const renderSubtitle = () => (
        <>
            <Paragraph marginBottom="lg">
                With Rent Guarantee coverage, you have the option of collecting a traditional security deposit, or
                waiving it and listing your rental as a “zero deposit” property  -- a selling point for renters who
                value financial flexibility.
            </Paragraph>
            <Paragraph fontType="bodyHeading">Rent Guarantee protects your investment, so you can:</Paragraph>
            <Paragraph>- Skip the hassle of receiving, holding, and refunding a deposit.</Paragraph>
            <Paragraph>- Offer an attractive, modern benefit that helps your property stand out to renters.</Paragraph>
        </>
    );

    const handleOnNextClick = () => {
        if (securityDepositChoice === REQUIRE_SECURITY_DEPOSIT && securityDeposit) {
            dispatch(RentGuaranteeActions.setSecurityDeposit(securityDeposit));
        }
        onGoToNextStep();
    };

    return (
        <WizardBodyTemplate
            title="Security deposit?"
            subtitle={renderSubtitle()}
        >
            <Row marginTop="xl" marginBottom="md">
                <Col xs={6}>
                    <RadioButton
                        onClick={() => setSecurityDepositChoice(WAIVE_SECURITY_DEPOSIT)}
                        name="security-deposit-choice"
                        id="waive-security-deposit"
                        label="Waive security deposit"
                        checked={securityDepositChoice === WAIVE_SECURITY_DEPOSIT}
                    />
                </Col>
                <Col xs={6}>
                    <RadioButton
                        onClick={() => setSecurityDepositChoice(REQUIRE_SECURITY_DEPOSIT)}
                        name="security-deposit-choice"
                        id="require-security-deposit"
                        label="Require security deposit"
                        checked={securityDepositChoice === REQUIRE_SECURITY_DEPOSIT}
                    />
                </Col>
            </Row>
            {securityDepositChoice === REQUIRE_SECURITY_DEPOSIT && (
                <FormField
                    label={<Label>Security deposit</Label>}
                    control={(
                        <AdornedInput
                            leftAdornment={<Adornment>$</Adornment>}
                            input={(
                                <CurrencyInput
                                    value={securityDeposit}
                                    onChange={onSecurityDepositChange}
                                />
                            )}
                        />
                    )}
                    description={<FormHelp>Amount should generally not exceed 150% of the base rent</FormHelp>}
                />
            )}
            <WizardActionFooter
                onBackClick={onGoToPrevStep}
                onNextClick={handleOnNextClick}
                isNextDisabled={!securityDepositChoice}
            />
        </WizardBodyTemplate>
    );
};

VerifySecurityDeposit.propTypes = propTypes;
VerifySecurityDeposit.defaultProps = defaultProps;

export default VerifySecurityDeposit;
